/**
 * LE TABLEAU « CONTENUS » — refonte de la console, lot L2 (maquette
 * `docs/pilotage/console-refonte/maquettes/02-contenus.html`).
 *
 * Tout ce que l'écran calcule vit ici, pour être éprouvé sans rendu : les
 * filtres (niveau, matière, socle, recherche), les totaux du haut, et ce qu'une
 * cellule dit (« 12 / 64 relus · 3 à corriger »). Aucun pourcentage à l'écran :
 * des comptes (règle de la console, `lib/programme-format.ts`).
 */
import {
  chapitreEstUnSocle,
  type ChapitreProgrammeResumeDto,
  type ComptesRelectureDto,
  type Matiere,
  type NiveauScolaire,
  type RelectureDuChapitreDto,
} from '@silex/shared';

export const SORTES_CONTENU = ['exercices', 'fiches', 'capsules', 'erreurs'] as const;
export type SorteContenu = (typeof SORTES_CONTENU)[number];

export const NOM_SORTE: Readonly<Record<SorteContenu, string>> = {
  exercices: 'Exercices',
  fiches: 'Fiches',
  capsules: 'Capsules',
  erreurs: 'Erreurs et remèdes',
};

export const NIVEAUX_FILTRE: readonly {
  readonly v: NiveauScolaire | '';
  readonly label: string;
}[] = [
  { v: '', label: 'Tous les niveaux' },
  { v: 'sec1', label: '1re sec.' },
  { v: 'sec2', label: '2e sec.' },
  { v: 'sec3', label: '3e sec.' },
  { v: 'sec4', label: '4e sec.' },
  { v: 'sec5', label: '5e sec.' },
];

export const MATIERES_FILTRE: readonly { readonly v: Matiere | ''; readonly label: string }[] = [
  { v: '', label: 'Toutes matières' },
  { v: 'mathematiques', label: 'Mathématiques' },
  { v: 'francais', label: 'Français' },
];

export interface FiltresContenus {
  readonly niveau: NiveauScolaire | '';
  readonly matiere: Matiere | '';
  readonly socle: boolean;
  readonly recherche: string;
}

export const FILTRES_PAR_DEFAUT: FiltresContenus = {
  niveau: '',
  matiere: '',
  socle: false,
  recherche: '',
};

/** Sans accents ni casse : « exposants » trouve « Les Exposants ». */
const plat = (t: string): string =>
  t
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

export function filtrerChapitres<T extends ChapitreProgrammeResumeDto>(
  chapitres: readonly T[],
  f: FiltresContenus,
): T[] {
  const q = plat(f.recherche.trim());
  return chapitres.filter(
    (c) =>
      (f.niveau === '' || c.niveau === f.niveau) &&
      (f.matiere === '' || c.matiere === f.matiere) &&
      (f.socle || !chapitreEstUnSocle(c)) &&
      (q === '' || plat(c.titre).includes(q) || plat(c.code).includes(q)),
  );
}

const ZERO: ComptesRelectureDto = { total: 0, enService: 0, relus: 0, aCorriger: 0 };

const ajouter = (a: ComptesRelectureDto, b: ComptesRelectureDto): ComptesRelectureDto => ({
  total: a.total + b.total,
  enService: a.enService + b.enService,
  relus: a.relus + b.relus,
  aCorriger: a.aCorriger + b.aCorriger,
});

/**
 * Les totaux du haut, sur les chapitres AFFICHÉS. `null` si le serveur n'a
 * pas rendu les comptes (serveur d'avant la refonte) : l'écran le dit au lieu
 * d'afficher des zéros qui mentiraient.
 */
export function totauxDeRelecture(
  chapitres: readonly ChapitreProgrammeResumeDto[],
): RelectureDuChapitreDto | null {
  if (chapitres.length > 0 && chapitres.every((c) => c.relecture === undefined)) return null;
  let t: RelectureDuChapitreDto = { exercices: ZERO, fiches: ZERO, capsules: ZERO, erreurs: ZERO };
  for (const c of chapitres) {
    if (c.relecture === undefined) continue;
    t = {
      exercices: ajouter(t.exercices, c.relecture.exercices),
      fiches: ajouter(t.fiches, c.relecture.fiches),
      capsules: ajouter(t.capsules, c.relecture.capsules),
      erreurs: ajouter(t.erreurs, c.relecture.erreurs),
    };
  }
  return t;
}

/** Ce qui reste à lire : ni relu, ni marqué à corriger. */
export const nonRelus = (c: ComptesRelectureDto): number => c.total - c.relus - c.aCorriger;

const nf = new Intl.NumberFormat('fr-CA');
export const nombre = (n: number): string => nf.format(n);

/** « 12 / 64 » — relus sur total. */
export const relusSurTotal = (c: ComptesRelectureDto): string =>
  `${nombre(c.relus)} / ${nombre(c.total)}`;

/** « 3 à corriger » — ou rien. */
export const direACorriger = (c: ComptesRelectureDto): string | null =>
  c.aCorriger > 0 ? `${nombre(c.aCorriger)} à corriger` : null;

/**
 * L'état de relecture d'un chapitre, en une étiquette : ce qui compte pour
 * savoir où aller. « À corriger » d'abord (un relecteur a trouvé quelque chose),
 * puis « À relire » tant qu'un contenu n'a été lu par personne, sinon « Relu ».
 */
export function etatDeRelecture(r: RelectureDuChapitreDto | undefined): {
  readonly label: string;
  readonly tone: 'acquis' | 'en-cours' | 'a-revoir';
} | null {
  if (r === undefined) return null;
  const sortes = SORTES_CONTENU.map((s) => r[s]);
  if (sortes.some((c) => c.aCorriger > 0)) return { label: 'À corriger', tone: 'a-revoir' };
  if (sortes.some((c) => nonRelus(c) > 0)) return { label: 'À relire', tone: 'en-cours' };
  return { label: 'Relu', tone: 'acquis' };
}

/** L'adresse de la file de relecture des exercices d'un chapitre (L3). */
export const adresseRelectureExercices = (code: string): string =>
  `/contenus/${encodeURIComponent(code)}/exercices`;
